import React from 'react';
import { clsx } from 'clsx';
import { Button } from 'src/components/ui/button';
import { Collapsible } from 'src/components/ui/collapsible';
import { Separator } from 'src/components/ui/separator';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';
import ChevronDownIcon from 'src/assets/img/chevron-down-icon.svg';
import ChevronDownDisabledIcon from 'src/assets/img/chevron-down-disabled-icon.svg';

// Shared by the Zendesk forms' expand/collapse panels (manual connect, ticket filters).
export const COLLAPSIBLE_TRIGGER_CLASS_NAME =
  'group flex w-full items-center gap-2 rounded-lg border border-grey_300 bg-white px-3 py-2 text-sm font-medium text-grey_750 transition-colors enabled:hover:border-green_750 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green_750/20 data-[state=open]:border-green_750 data-[state=open]:shadow-[0_0_2px_0_rgba(96,235,139,0.2)] disabled:cursor-not-allowed disabled:border-border_subtle disabled:text-grey_300';

export const COLLAPSIBLE_CONTENT_CLASS_NAME =
  'overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up';

// "Connect with Zendesk" (OAuth) with a collapsible "Connect manually with API token" alternative.
// The manual credential fields are passed as children because each form names them differently.
export const ZendeskConnectSection = ({
  isZendeskAuthorized,
  isManualConnectActive,
  onConnectWithZendesk,
  onManualConnectOpenChange,
  className,
  children,
}: {
  isZendeskAuthorized: boolean;
  isManualConnectActive: boolean;
  onConnectWithZendesk: () => void;
  onManualConnectOpenChange: (open: boolean) => void;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={clsx('flex flex-col gap-2', className)}>
    <Button
      type={'button'}
      variant={isZendeskAuthorized ? 'ghost-accent' : 'dark'}
      onClick={onConnectWithZendesk}
      disabled={isManualConnectActive || isZendeskAuthorized}
      className={clsx(
        'w-full px-8 h-[38px]',
        isZendeskAuthorized &&
          'bg-green_150 font-medium disabled:opacity-100 disabled:pointer-events-none',
      )}
    >
      {isZendeskAuthorized ? (
        'Connected successfully with Zendesk'
      ) : (
        <>
          Connect with Zendesk
          <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
        </>
      )}
    </Button>
    <p className={'text-center text-[13px] font-medium text-tertiary'}>
      You’ll be redirected to Zendesk to authorize access.
    </p>
    <div className={'flex items-center gap-2'}>
      <Separator className={'flex-1 bg-border_subtle'} />
      <span className={'text-[13px] font-medium text-tertiary'}>or</span>
      <Separator className={'flex-1 bg-border_subtle'} />
    </div>
    <Collapsible
      open={isManualConnectActive}
      onOpenChange={onManualConnectOpenChange}
      disabled={isZendeskAuthorized}
      triggerClassName={COLLAPSIBLE_TRIGGER_CLASS_NAME}
      trigger={
        <>
          <span className={'flex-1 text-center'}>Connect manually with API token</span>
          {isZendeskAuthorized ? (
            <img src={ChevronDownDisabledIcon} alt={''} width={16} height={16} />
          ) : (
            <img
              src={ChevronDownIcon}
              alt={''}
              width={12.8}
              height={12.8}
              className={'transition-transform duration-200 group-data-[state=open]:rotate-180'}
            />
          )}
        </>
      }
      contentClassName={COLLAPSIBLE_CONTENT_CLASS_NAME}
    >
      <div className={'flex flex-col gap-2 pt-2'}>{children}</div>
    </Collapsible>
  </div>
);
